import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import { useDispatch, useSelector } from "react-redux";
import { AdminFormFieldSubmit } from "../../../components/admin/formFields";
import TickCheckbox from "../../../components/tickCheckbox";
import {
  useGetDeviceGeneralInfoQuery,
  useLazyGetDeviceGeneralInfoQuery,
  useLazyGetDeviceWakeupQuery,
} from "../../../api/network/vmApiService";
import { toast } from "react-toastify";
import { CONNECTIVITY_STATE } from "../constants";
import { E } from "../../../components/surfsight-ui/www/build/index-8716963f";
import svgIcons from "../../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../../constants/constants";

type ModalWakeupCameraProps = {
  cameraId: string;
  serialNum: string;
  // deprecated, callbacks are moved to redux store
  // onWakeupSuccess: (serialNum: string) => void;
  // onWakeupFailure?: (error: string) => void;
};

const ModalWakeupCamera: FC<ModalWakeupCameraProps> = ({
  cameraId,
  serialNum,
  // deprecated, callbacks are moved to redux store
  // onWakeupSuccess,
  // onWakeupFailure = () => {},
}) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix:
      "camera.dashboard.management.cameras.camera_details.tabs.overview",
  });
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const dispatch = useDispatch();
  const [timerStart, setTimerStart] = useState(false); // Flag to initiate timer
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [wakeupApiTrigger, { isLoading: isLoadingWakeup }] =
    useLazyGetDeviceWakeupQuery();

  const [
    deviceInfoApiTrigger,
    {
      currentData: dataDeviceInfo,
      isLoading: isLoadingDeviceInfo,
      isUninitialized,
    },
  ] = useLazyGetDeviceGeneralInfoQuery({
    pollingInterval: 1 * 1000, // 1 second
  });

  useEffect(() => {
    if (isUninitialized || isLoadingDeviceInfo || !timerStart) return;

    console.log(
      "Checking device info...",
      dataDeviceInfo?.data[0]?.connectivity_state
    );

    if (dataDeviceInfo?.data && dataDeviceInfo?.data?.length > 0) {
      const infoData = dataDeviceInfo?.data[0];
      if (infoData.connectivity_state === CONNECTIVITY_STATE.waking_up) {
        // Device is waking up, Set flag tostart timer to wait for 5 sec.(Set flag )
        if (!timerStart) {
          setTimerStart(true);
        }
      } else if (infoData.connectivity_state === CONNECTIVITY_STATE.online) {
        // Device is online
        reset(); // Reset timer and flag
        toast.info(
          "Device is online and connecting with servers. Please wait..."
        );
        setTimeout(() => {
          // onWakeupSuccess(serialNum)
          modalsState.showModalWakeupCamera?.onSuccessCallback?.(serialNum);
        }, 1000);
        if (APP_CONFIG.DEBUG.ALL) console.log("calling hideModal A");
        hideModal();
      } else if (infoData.connectivity_state === CONNECTIVITY_STATE.offline) {
        toast.error(
          "Unable to wake up device. The device may have a poor Internet connection or it isn't powered on."
        );
        reset();
        // onWakeupFailure(
        //   "Unable to wake up device. The device may have a poor Internet connection or it isn't powered on."
        // );
        modalsState.showModalWakeupCamera?.onFailureCallback?.(
          "Unable to wake up device. The device may have a poor Internet connection or it isn't powered on."
        );
        if (APP_CONFIG.DEBUG.ALL) console.log("calling hideModal B");
        hideModal();
      }
    }
    //  else {
    //   reset();
    //   if (APP_CONFIG.DEBUG.ALL) console.log("calling hideModal C");
    //   hideModal();
    // }
  }, [
    dataDeviceInfo,
    isUninitialized,
    isLoadingDeviceInfo,
    timerStart,
    serialNum,
  ]);

  const reset = () => {
    setTimerStart(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const hideModal = () => {
    setTimeout(() => {
      dispatch(setModalsData({ ...modalsState, showModalWakeupCamera: null }));
    }, 500); // Delay to allow any pending state reset to complete
  };

  const handleSubmit = () => {
    wakeupApiTrigger(cameraId)
      .then((resposne: any) => {
        // Get device info. and proceed accordingly
        deviceInfoApiTrigger(cameraId);
        setTimerStart(true);
      })
      .catch((error) => {
        console.log(error);
        toast.error("Error waking up: ", error);
        // onWakeupFailure(`Error waking up: ${error}`);
        modalsState.showModalWakeupCamera?.onFailureCallback?.(
          `Error waking up: ${error}`
        );
        if (APP_CONFIG.DEBUG.ALL) console.log("calling hideModal D");
        hideModal();
      });
  };

  const handleCancel = () => {
    modalsState.showModalWakeupCamera?.onCancelCallback?.();
    hideModal();
  };

  if (modalsState.showModalWakeupCamera === null) return null;
  return (
    <>
      <div
        className="fixed w-full h-screen top-0 left-0 bg-modal-overlay z-overlay"
        onClick={hideModal}
      ></div>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 outline-none focus:outline-none z-modal">
        <div className="relative my-6 mx-auto max-w-[calc(100vw-4rem)] w-[480px] z-modal">
          <form
            className="p-8 bg-white grid grid-cols-6 gap-6 rounded-3xl shadow-2xl"
            onSubmit={handleSubmit}
          >
            <h2 className="col-span-6 text-3xl font-bold text-heading-black">
              {t("wakeup_device")}
            </h2>
            <p className="col-span-6 mt-0 leading-relaxed text-gray-500">
              {t("wakeup_msg")}
            </p>
            <AdminFormFieldSubmit
              label={
                isLoadingDeviceInfo || timerStart ? (
                  <img
                    src={svgIcons.LoadingAnimation}
                    alt="Loading animation"
                    className={`size-10`}
                  />
                ) : (
                  t("wakeup_submit")
                )
              }
              type="button"
              variant="primary"
              disabled={isLoadingWakeup || timerStart || isLoadingDeviceInfo}
              onClick={handleSubmit}
            />

            <AdminFormFieldSubmit
              label={tMain("no")}
              type="button"
              variant="danger-transparent"
              onClick={handleCancel}
            />
          </form>
        </div>
      </div>
    </>
  );
};

export default ModalWakeupCamera;
